import { useContext, useEffect, useRef } from "react";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { useBranding } from "../../providers/BrandingProvider";
import { absoluteURL } from "../../utils/helpers";

/**
 * Auto-logs the user out after a period of inactivity. Mounted at the
 * top of the Menubar so it ticks on every authenticated page.
 *
 * The idle threshold is resolved most-specific first:
 *   1. the current project's `session_idle_timeout_minutes` (>0), else
 *   2. the active org's `session_idle_timeout_minutes` (>0, from the
 *      whoami payload's `active_organization_meta`), else
 *   3. the platform-wide default `session_idle_timeout_default_minutes`
 *      (from Platform Settings, surfaced on the branding payload).
 * A 0 at a level means "inherit the next level down". All 0 = disabled.
 * A signed-in user with no project open still gets the org/platform
 * value.
 *
 * "Activity" = mousemove, keydown, click, scroll, touchstart. Each one
 * resets a single setTimeout. When the timer fires we navigate to
 * /logout (the same flow as the user-menu logout button); the proxy +
 * the server take care of the rest.
 *
 * Implementation notes:
 * - We use `setTimeout` rather than tracking last-activity in a
 *   loop so the cost is one timer per activity burst, not a poll.
 * - Listeners are passive ({ passive: true }) so they don't slow
 *   down scroll/mousemove paint.
 * - Cleaning up on unmount is critical — without it the timer
 *   fires on a stale closure after the project changes.
 */
export const IdleLogout = () => {
  const { project } = useContext(ProjectContext);
  const branding = useBranding();
  const { user } = useAuth();
  const projectMinutes = Number(project?.session_idle_timeout_minutes ?? 0);
  const orgMinutes = Number(user?.active_organization_meta?.session_idle_timeout_minutes ?? 0);
  const platformDefault = Number(branding?.session_idle_timeout_default_minutes ?? 0);
  // Resolution: project override > org override > platform default.
  // A 0 at any level means "inherit the next one down".
  const minutes = projectMinutes > 0 ? projectMinutes : orgMinutes > 0 ? orgMinutes : platformDefault;
  const timerRef = useRef(null);

  useEffect(() => {
    if (!minutes || minutes <= 0) return undefined;

    const timeoutMs = minutes * 60 * 1000;

    const armTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Identical to clicking "Sign out" in the user dropdown.
        window.location.href = absoluteURL("/logout");
      }, timeoutMs);
    };

    const onActivity = () => armTimer();

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    armTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [minutes]);

  return null;
};
